import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [devToken, setDevToken] = useState<string | null>(null);
  const [autoVerifying, setAutoVerifying] = useState(false);
  const [searchParams] = useSearchParams();
  const autoVerifyAttempted = useRef(false);

  const { login, verifyToken, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  // Check for token in URL (from magic link email)
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken && !autoVerifyAttempted.current) {
      autoVerifyAttempted.current = true;
      setToken(urlToken);
      setStep('verify');
      setAutoVerifying(true);
      
      verifyToken(urlToken)
        .then(() => {
          navigate('/');
        })
        .catch(() => {
          setAutoVerifying(false);
        });
    }
  }, [searchParams, verifyToken, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      const result = await login(email);
      console.log('Login response:', result); // Debug log
      
      if (result?.devToken) {
        setDevToken(result.devToken);
        setToken(result.devToken);
        // Auto-verify in dev mode
        setAutoVerifying(true);
        try {
          await verifyToken(result.devToken);
          navigate('/');
        } catch {
          setAutoVerifying(false);
        }
      } else {
        // No devToken - email was sent, show verify step
        setStep('verify');
      }
    } catch (err) {
      console.error('Login error:', err);
      // Error is handled by the store
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!token.trim()) {
      return;
    }

    setAutoVerifying(true);
    try {
      await verifyToken(token);
      navigate('/');
    } catch {
      setAutoVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-950 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary-600/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-600 mb-4">
            <svg className="h-10 w-10 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
            </svg>
          </div>
          <h1 className="font-display text-3xl font-bold text-white">MIA Voice Platform</h1>
          <p className="mt-2 text-surface-400">AI-powered voice assistant for real estate</p>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body">
            {step === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-surface-300 mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="you@example.com"
                    required
                    autoFocus
                    disabled={isLoading || autoVerifying}
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={isLoading || autoVerifying}
                >
                  {isLoading || autoVerifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {autoVerifying ? 'Logging in...' : 'Sending...'}
                    </span>
                  ) : (
                    'Send Magic Link'
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-500/20 text-accent-400 mb-4">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-white">Check your email</h2>
                  <p className="mt-1 text-sm text-surface-400">
                    We sent a magic link to <span className="text-surface-200">{email}</span>
                  </p>
                </div>

                {devToken && (
                  <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="h-4 w-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <p className="text-sm font-medium text-green-400">Development Mode</p>
                    </div>
                    <p className="text-xs text-green-300/70">
                      Token auto-filled for testing
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="token" className="block text-sm font-medium text-surface-300 mb-2">
                    Verification token
                  </label>
                  <input
                    type="text"
                    id="token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    className="input font-mono text-sm"
                    placeholder="Paste token from email..."
                    disabled={isLoading || autoVerifying}
                  />
                  <p className="mt-2 text-xs text-surface-500">
                    Check your email for the magic link, or paste the token here
                  </p>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setDevToken(null);
                      setToken('');
                      setAutoVerifying(false);
                      clearError();
                    }}
                    className="btn-secondary flex-1"
                    disabled={isLoading || autoVerifying}
                  >
                    Back
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary flex-1" 
                    disabled={isLoading || autoVerifying || !token.trim()}
                  >
                    {isLoading || autoVerifying ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Verifying...
                      </span>
                    ) : (
                      'Verify'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-surface-500">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
